import { useState } from 'react';
import Link from 'next/link';
import { useParams, usePathname } from 'next/navigation';

import { PopoverButton } from '@headlessui/react';
import clsx from 'clsx';

import { formatUrlPathname } from '@/utils/urls/formatUrl';

import Icon from '@/components/base/Icon';
import { PopoverContainer, PopoverWrapper } from '@/components/base/Popover';
import type { DropdownLinksType } from '@/components/layouts/LayoutPlatform/components/PageHeaderRecap';

type PopoverListProps = {
    links: DropdownLinksType;
    isSticky?: boolean;
};

export function PopoverList({ links, isSticky }: PopoverListProps) {
    const pathname = usePathname();
    const params = useParams();

    const localStoragePingPopover = localStorage.getItem('ping-popover');
    const [displayPing, setDisplayPing] = useState(!localStoragePingPopover);

    const disablePing = () => {
        setDisplayPing(false);
        localStorage.setItem('ping-popover', 'false');
    };

    if (!links) return null;

    return (
        <PopoverWrapper
            popperOptions={{
                placement: 'bottom-start',
                offset: [-8, 8]
            }}>
            {({ open, setReferenceElement, setPopperElement, styles, attributes }) => (
                <>
                    <PopoverButton
                        ref={setReferenceElement}
                        as="button"
                        onClick={disablePing}
                        className="relative flex items-center bg-icoGray-300 transition-colors duration-300 dark:bg-icoDarkMode-wolf"
                        data-testid="popover-button">
                        {({ open }) => (
                            <>
                                <Icon
                                    icon="chevron-bottom"
                                    className={clsx(
                                        'cursor-pointer p-0.5 text-icoGray-500 transition-transform duration-300 dark:text-icoDarkMode-wolf',
                                        { 'rotate-180': open, 'text-8': isSticky }
                                    )}
                                />
                                {displayPing ? (
                                    <span className="absolute left-0 top-0 -z-1 flex h-full w-full">
                                        <span className="absolute inline-flex h-full w-full animate-ping-small rounded bg-icoBlue/75"></span>
                                    </span>
                                ) : null}
                            </>
                        )}
                    </PopoverButton>

                    <PopoverContainer
                        open={open}
                        setPopperElement={setPopperElement}
                        styles={styles}
                        attributes={attributes}
                        width="w-32"
                        className="min-w-fit rounded-md bg-white p-1 shadow-icoM dark:border dark:border-icoDarkMode-beton dark:bg-icoDarkMode-deepBlack">
                        <ul className="flex flex-col gap-1">
                            {links.map(({ href, label, restricted, onClick }) => (
                                <li
                                    key={href}
                                    className="flex h-7.5 text-icoGray-500 dark:text-icoDarkMode-wolf">
                                    <Link
                                        href={href}
                                        onClick={onClick}
                                        className={clsx(
                                            'flex h-full w-full items-center gap-1 rounded-m px-2 text-xs transition-all duration-300 hover:bg-icoBlue-100 hover:text-icoBlue-400 hover:dark:bg-icoBlue/20',
                                            {
                                                'pointer-events-none text-icoBlue':
                                                    pathname === formatUrlPathname(href, params)
                                            }
                                        )}>
                                        {label}
                                        {restricted && (
                                            <Icon
                                                icon="crown"
                                                className="text-icoBlue"
                                                data-testid="upsell-icon"
                                            />
                                        )}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </PopoverContainer>
                </>
            )}
        </PopoverWrapper>
    );
}
